import { defineElement, html, i18n, TemplateElement } from '@webtides/element-js';
import styles from './social-share-dialog.css';

const copyTextToClipboard = text => {
    const textArea = document.createElement('textarea');
    textArea.value = text;

    // Avoid scrolling to bottom
    textArea.style.top = '0';
    textArea.style.left = '0';
    textArea.style.position = 'fixed';

    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();

    let success = false;
    try {
        success = document.execCommand('copy');
    } catch (err) {}

    document.body.removeChild(textArea);

    return success;
};

class SocialShareDialog extends TemplateElement {
    constructor() {
        super({ styles: [styles] });
    }

    properties() {
        return {
            title: i18n('buybox.title'),

            shareTitle: '',
            shareBody: '',
            shareLink: window.location.href,

            shareOptions: [
                {
                    name: 'whatsapp',
                    text: i18n('sharing.dialog.whatsapp', 'Per Whatsapp teilen'),
                    classes: 'xl:hidden',
                },
                { name: 'mail', text: i18n('sharing.dialog.email', 'Per E-Mail teilen') },
                { name: 'facebook', text: i18n('sharing.dialog.facebook', 'Auf Facebook teilen') },
                { name: 'copy-link', text: i18n('sharing.dialog.copy', 'Link kopieren') },
            ],

            copySuccess: undefined,
        };
    }

    connected() {
        this.removeAttribute('cloak');
    }

    events() {
        return {
            '[copy-link]': {
                click: ev => {
                    this.copySuccess = copyTextToClipboard(this.shareLink);

                    setTimeout(() => {
                        this.copySuccess = undefined;
                    }, 3000);
                },
            },
        };
    }

    getShareBody() {
        return `${this.shareBody}\r\n${this.shareLink}`;
    }

    templateCopyResult() {
        return html`
            <svg-icon
                class="inline-block ml-4 w-5 h-5 ${this.copySuccess ? 'text-status-green' : 'text-status-red'}"
                name="${this.copySuccess ? 'check' : 'close'}"
            >
            </svg-icon>
        `;
    }

    templateOptionContent({ name, text }) {
        return html`
            <div class="flex items-center space-x-4 py-3">
                <svg-icon class="inline-block w-4 h-4" name="${name}"></svg-icon>
                <span class="inline-flex items-center">
                    <span> ${text} </span>
                    ${name === 'copy-link' && typeof this.copySuccess !== 'undefined'
                        ? this.templateCopyResult()
                        : html``}
                </span>
            </div>
        `;
    }

    templateShareOption(option) {
        const { name } = option;

        switch (name) {
            case 'whatsapp':
                return html`
                    <a href="whatsapp://send?text=${this.shareLink}">${this.templateOptionContent(option)}</a>
                `;
            case 'mail':
                return html`
                    <a
                        href="mailto:?body=${encodeURIComponent(this.getShareBody())}&subject=${encodeURIComponent(
                            this.shareTitle
                        )}"
                    >
                        ${this.templateOptionContent(option)}
                    </a>
                `;
            case 'facebook':
                return html`
                    <a href="https://facebook.com/sharer.php?display=page&u=${this.shareLink}" target="_blank">
                        ${this.templateOptionContent(option)}
                    </a>
                `;
            case 'copy-link':
                return html` <a copy-link>${this.templateOptionContent(option)}</a> `;
        }
    }

    template() {
        return html`
            <modal-element
                cloak="display-none"
                disable-footer="true"
                minimal-ui="true"
                fit-content="true"
                ref="modal"
                modal-id="social-share"
            >
                <div slot="title" class="text-center pt-6">
                    <h2 class="inline-flex items-center space-x-4">
                        <svg-icon class="inline-block w-6 h-6 text-grey-300" name="share"></svg-icon>
                        <div class="font-bold text-grey-800">${this.title}</div>
                    </h2>
                </div>
                <div class="px-4 pb-4 pt-0 md:pb-10 md:px-8 xl:px-10 xl:pb-10" slot="content">
                    <div>
                        ${this.shareOptions.map(option => {
                            return html`<div
                                class="share-link border-b border-grey-200 last:border-b-0 ${option.classes ?? ''}"
                            >
                                ${this.templateShareOption(option)}
                            </div>`;
                        })}
                    </div>

                    <div class="mt-3">
                        <button class="btn btn-light justify-center w-full" type="button" data-modal-close>
                            ${i18n('cancel', 'Abbrechen')}
                        </button>
                    </div>
                </div>
            </modal-element>
        `;
    }
}

defineElement('social-share-dialog', SocialShareDialog);
